import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { ExerciseTag, IExerciseInvite } from "@hulanbv/platformapp";
import { style } from "typestyle";
import { IHttpOptions } from "nest-utilities-client";
import { useHistory } from "react-router-dom";
import { exerciseInviteService } from "../../../state/exercise-invite/exercise-invite.service";
import { PageBody } from "../../elements/page-body.element";
import { Page } from "../../elements/page.element";
import previewImage from "../../../assets/graphics/illustrations/client-timeline-preview.svg";
import { ReactComponent as searchIcon } from "../../../assets/graphics/symbols/search.svg";
import { ReactComponent as bookIcon } from "../../../assets/graphics/symbols/book.svg";
import { ClientTimelinePreview } from "../../templates/client-timeline-preview.template";
import { useAuthContext } from "../../../state/authentication/authentication.context";
import { ReactComponent as ChatSvg } from "../../../assets/graphics/symbols/chat.svg";
import { Header } from "../../elements/header.element";
import { dictionary } from "../../../state/common/constants/dictionary.constants";
import { branding } from "../../../constants/branding.constants";
import { VideoPlayer } from "../../elements/video-player/video-player.element";
import { Button } from "../../elements/button.element";
import { routes } from "../../../state/common/constants/routes.constants";
import { graphicSet } from "../../../constants/graphic-set.constants";
import { GeometricShape } from "../../elements/geometric-shape";
import { SvgIcon } from "../../elements/svg-icon.element";
import { Anchor } from "../../elements/anchor.element";
import { exerciseService } from "../../../state/exercise/exercise.service";
import { LoadingSpinner } from "../../elements/loading-spinner.element";

// eslint-disable-next-line complexity -- We need to have this many conditions
export const HomeClientScreen: FC = () => {
  const { session } = useAuthContext();
  const history = useHistory();

  const [callToActions, setCallToActions] = useState<ReactNode[] | null>(null);

  useEffect(() => {
    // If the user has a practitioner, we don't show the call to actions
    if (session?.user?.practitioner) {
      setCallToActions([]);
      return;
    }

    const hydrateCallToActions = async () => {
      const actions = [
        {
          label: branding.features?.externalPurchaseButton?.title,
          icon: bookIcon,
          color: "rgb(var(--rgb-color-primair-lighter))",
          url: branding.features?.externalPurchaseButton?.url,
          condition: () => branding.features?.externalPurchaseButton,
        },
        {
          label: dictionary.literals.chatWithGijsAi,
          icon: ChatSvg,
          color: "rgb(var(--rgb-color-primair-lighter))",
          url: routes.platformSpecificContent.path,
          condition: async () => {
            const hasBooks = await exerciseService.getAll({
              match: {
                tags: ExerciseTag.PLATFORM_SPECIFIC,
              },
              limit: 1,
            });

            return hasBooks.data.length > 0;
          },
        },
        {
          label: dictionary.literals.books,
          icon: bookIcon,
          color: "rgb(var(--rgb-color-primair-lighter))",
          url: routes.bookContent.path,
          condition: async () => {
            const hasBooks = await exerciseService.getAll({
              match: {
                tags: ExerciseTag.BOOK,
              },
              limit: 1,
            });

            return hasBooks.data.length > 0;
          },
        },
        {
          label: dictionary.literals.freeContent,
          icon: graphicSet.trainingsGraphic,
          color: "rgb(var(--rgb-color-primair-lighter))",
          url: routes.clientIntroductionContent.path,
          condition: async () => {
            if (!branding.features?.hasIntroductionContent) {
              return false;
            }
            const hasBooks = await exerciseService.getAll({
              match: {
                tags: ExerciseTag.INTRODUCTION_CONTENT,
              },
              limit: 1,
            });

            return hasBooks.data.length > 0;
          },
        },
        {
          label: branding.features?.externalSearch?.title,
          icon: searchIcon,
          color: "rgb(var(--rgb-color-primair-lighter))",
          url: branding.features?.externalSearch?.url,
          condition: () => branding.features?.externalSearch,
        },
      ];

      const allowedActions: typeof actions = [];
      for (const action of actions) {
        // eslint-disable-next-line no-await-in-loop -- We need to await the condition for each action
        const isAllowed = await action.condition();
        if (isAllowed) {
          allowedActions.push(action);
        }
      }

      setCallToActions(
        allowedActions
          .filter((action) => action.condition())
          .map((action, index) => (
            <Anchor
              href={action.url ?? "#"}
              key={index}
              className={styles.geometricShapeContainer}
            >
              <GeometricShape
                color={"rgb(var(--rgb-color-primair-lighter))"}
                className={styles.geometricShapeSide}
              >
                <SvgIcon icon={action.icon} width={40} height={40} />
              </GeometricShape>
              <div className={styles.geometricShapeLabel}>{action.label}</div>
            </Anchor>
          )),
      );
    };
    hydrateCallToActions();
  }, [session?.user?.practitioner]);

  const [highlightedExerciseInvite, setHighlightedExerciseInvite] =
    useState<IExerciseInvite | null>(null);

  useEffect(() => {
    const exerciseInviteOptions: IHttpOptions<IExerciseInvite> = {
      limit: 1,
      sort: ["-scheduledDate"],
      populate: ["exercise"],
      match: {
        scheduledDate: { $lt: new Date().toISOString() },
      },
    };

    const getHighlight = async () => {
      const unseenInvitesOptions: IHttpOptions<IExerciseInvite> = {
        ...exerciseInviteOptions,
        ...{
          match: {
            ...exerciseInviteOptions.match,
            seenAt: { $exists: false },
          },
        },
      };

      const { data: lastUnseenInvites } = await exerciseInviteService.getAll(
        unseenInvitesOptions,
      );

      if (lastUnseenInvites.length) {
        setHighlightedExerciseInvite(lastUnseenInvites[0]);
        return;
      }

      const { data: lastInvites } = await exerciseInviteService.getAll(
        exerciseInviteOptions,
      );
      setHighlightedExerciseInvite(lastInvites[0] ?? null);
    };

    getHighlight();
  }, [session?.userId]);

  const clientTimeLineRef = useRef<HTMLDivElement>(null);

  return (
    <Page hideGoBack>
      <PageBody
        fullWidth={session?.user?.practitioner !== null}
        className={session?.user?.practitioner ? styles.noSpacing : undefined}
      >
        {callToActions === null && <LoadingSpinner color="black" />}

        {!session?.user?.practitioner && callToActions !== null && (
          <>
            <div className={styles.navButtonsContainer}>{callToActions}</div>

            <div className={styles.introductionContainer}>
              <Header className={styles.header} center>
                {dictionary.texts.joinAppForFree(branding.name ?? "PLAP")}
              </Header>
              {branding.videoUrls?.introduction && (
                <VideoPlayer url={branding.videoUrls.introduction} />
              )}
              <div>
                {branding.restrictions?.canRegisterAsProfessional !== false && (
                  <Button
                    attributes={{
                      onClick: () =>
                        history.push(routes.registerPractitioner.path),
                    }}
                  >
                    {dictionary.texts.clickToRegisterAsProfessional}
                  </Button>
                )}
                {highlightedExerciseInvite?.exercise && (
                  <div className={styles.container} ref={clientTimeLineRef}>
                    <ClientTimelinePreview
                      exerciseInvite={highlightedExerciseInvite}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {session?.user?.practitioner && callToActions !== null && (
          <div className={styles.container} ref={clientTimeLineRef}>
            <Header center size="big" className={styles.header}>
              {dictionary.literals.myPracticeEnvironment}
            </Header>
            <div className={styles.previewImageContainer}>
              {branding.restrictions?.canSeeBannerImage !== false && (
                <img
                  src={previewImage}
                  alt="client-timeline"
                  className={styles.previewImage}
                />
              )}
              {!highlightedExerciseInvite?.exercise && (
                <div className={styles.textContainer}>
                  <p>{dictionary.texts.noExercisesReady}</p>
                </div>
              )}
            </div>

            {highlightedExerciseInvite?.exercise && (
              <ClientTimelinePreview
                exerciseInvite={highlightedExerciseInvite}
              />
            )}
          </div>
        )}
      </PageBody>
    </Page>
  );
};
const styles = {
  container: style({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(180deg, rgba(var(--rgb-color-primair-shade-4), 0.15), rgba(var(--rgb-color-primair-basis), 0.15))",
    padding: "35px 0",
  }),
  navButtonsContainer: style({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-around",
    gap: 20,
  }),
  geometricShapeContainer: style({
    display: "flex",
    flexDirection: "column",
    maxWidth: "33%",
    alignItems: "center",
    cursor: "pointer",
  }),
  halfWidth: style({
    maxWidth: "50%",
  }),
  geometricShapeLabel: style({
    textAlign: "center",
    fontSize: 12,
    paddingTop: 10,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "100%",
  }),
  geometricShapeSide: style({
    width: 95,
    height: 106,
  }),
  middleGeometricShape: style({
    width: 107.3,
    height: 120,
  }),
  header: style({
    marginBottom: 25,
  }),
  previewImageContainer: style({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "center",
  }),
  previewImage: style({
    width: "50%",
    margin: "0 auto",
    marginBottom: 25,
  }),
  textContainer: style({
    textAlign: "center",
    padding: "0px 50px",
  }),
  noSpacing: style({
    paddingTop: 0,
    paddingBottom: 0,
    height: "100%",
  }),
  introductionContainer: style({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 40,
    paddingTop: 20,
  }),
};
